import { createPortal } from "react-dom"
import { zIndex } from "@nova/constants"
import { useObserveElementRect, useTimeAgo } from "@nova/toolkit-pure-hooks"
import { useAppSelector } from "integrations/redux/hooks"
import { selectMapOverlaySceneTimes } from "../control/selectors"
import { OverlayKey } from "../control/types"
import { OVERLAY_INFO } from "./overlayInfo"

export type HoveredOverlayRow = {
    key: OverlayKey
    title: string
    rect: DOMRect
    overzoomed?: boolean
}

const PANEL_WIDTH = 230
const GAP = 12

export const MapFooterOverlayInfoPanel = ({ hovered }: { hovered: HoveredOverlayRow }) => {
    const info = OVERLAY_INFO[hovered.key]
    const { ref, height } = useObserveElementRect()
    const sceneTime = useAppSelector(selectMapOverlaySceneTimes)[hovered.key]
    const sceneTimeAgo = useTimeAgo({ timestamp: sceneTime })

    const portalRoot = document.getElementById("portal")

    if (!info || !portalRoot) return null

    const left = hovered.rect.right + GAP
    const topMax = window.innerHeight - 16 - height
    const rowMidY = hovered.rect.top + hovered.rect.height / 2
    const top = Math.max(8, Math.min(topMax, rowMidY - height / 2))
    const arrowY = Math.max(12, Math.min(height - 24, rowMidY - top - 6))

    return createPortal(
        <div
            ref={ref}
            className="pointer-events-none fixed flex flex-col items-start gap-1 p-3 rounded-lg border border-white-200 bg-black-800 shadow-[0_2px_3px_0_rgba(12,12,15,0.90),0_4px_16px_0_rgba(12,12,15,0.95)] backdrop-blur-sm"
            style={{ left, top, width: PANEL_WIDTH, zIndex: zIndex.MapLayerManager + 1 }}
        >
            <div
                style={{
                    position: "absolute",
                    right: "100%",
                    top: arrowY,
                    width: 0,
                    height: 0,
                    borderTop: "7px solid transparent",
                    borderBottom: "7px solid transparent",
                    borderRight: "7px solid rgba(255, 255, 255, 0.10)",
                }}
            />
            <div
                style={{
                    position: "absolute",
                    right: "calc(100% - 1px)",
                    top: arrowY + 1,
                    width: 0,
                    height: 0,
                    borderTop: "6px solid transparent",
                    borderBottom: "6px solid transparent",
                    borderRight: "6px solid rgb(12, 12, 15)",
                }}
            />

            <small className="xs text-gray-300">{hovered.title}</small>

            <p className="self-stretch text-sm leading-[140%] text-gray-100">{info.description}</p>

            {info.previewSrc ? (
                <img
                    src={info.previewSrc}
                    alt={hovered.title}
                    className="h-[120px] w-full self-stretch rounded-md object-cover"
                />
            ) : (
                <div className="h-[120px] self-stretch rounded-md bg-white-200" />
            )}

            <div className="py-2 flex flex-row flex-wrap gap-2">
                {info.tags.map((tag) => (
                    <div
                        key={tag}
                        className="flex items-center justify-center px-1 py-0.5 rounded-md bg-gray-700"
                    >
                        <small className="text-gray-100 leading-none whitespace-nowrap">
                            {tag}
                        </small>
                    </div>
                ))}
            </div>

            <div className="self-stretch border-t border-white-200 pb-2" />

            <div className="flex flex-col gap-[6px] self-stretch">
                <div className="flex flex-row justify-between">
                    <small className="leading-[18px] text-gray-400">
                        {sceneTime !== undefined ? "Updated:" : "Updates:"}
                    </small>
                    <small className="font-mono leading-[140%] text-white">
                        {sceneTime !== undefined ? sceneTimeAgo : info.cadence}
                    </small>
                </div>
                <div className="flex flex-row justify-between">
                    <small className="leading-[18px] text-gray-400">Source:</small>
                    <small className="font-mono leading-[140%] text-white">{info.source}</small>
                </div>
            </div>

            {hovered.overzoomed && (
                <>
                    <div className="self-stretch border-t border-white-200 pb-2" />
                    <small className="self-stretch leading-[140%] text-red-400">
                        Zoom out to view this layer.
                    </small>
                </>
            )}
        </div>,
        portalRoot
    )
}
